import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const COOKIE_CONSENT_KEY = 'wim_cookie_consent';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitorSessionId');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    sessionStorage.setItem('visitorSessionId', sessionId);
  }
  return sessionId;
};

// Check if analytics cookies are allowed
const isAnalyticsAllowed = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return false; // No consent given yet
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
};

export const VisitorTracker = () => {
  const location = useLocation();
  const heartbeatRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const [analyticsEnabled, setAnalyticsEnabled] = useState(isAnalyticsAllowed());
  const hasTrackedSession = useRef(false);

  // Track page view function - defined first
  const trackPageView = useCallback(async () => {
    if (!isAnalyticsAllowed()) {
      console.debug('Analytics tracking disabled by user preference');
      return;
    }

    try {
      const params = new URLSearchParams({
        session_id: sessionId.current,
        page_path: location.pathname,
        page_title: document.title,
        referrer: document.referrer || ''
      });

      await fetch(`${API}/track/session?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      hasTrackedSession.current = true;
      console.debug('Page view tracked:', location.pathname);
    } catch (error) {
      // Silently fail - tracking shouldn't break the app
      console.debug('Tracking error:', error);
    }
  }, [location.pathname]);

  // Listen for consent changes
  useEffect(() => {
    const checkConsent = () => {
      const allowed = isAnalyticsAllowed();
      setAnalyticsEnabled(allowed);
    };

    // Check on mount
    checkConsent();

    // Listen for storage changes (in case user updates consent in another tab)
    const handleStorageChange = (e) => {
      if (e.key === COOKIE_CONSENT_KEY) {
        checkConsent();
      }
    };

    // Listen for custom consent change event (same tab)
    const handleConsentChange = (e) => {
      console.debug('Cookie consent changed:', e.detail);
      checkConsent();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cookieConsentChanged', handleConsentChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  // Track when analytics becomes enabled or page changes
  useEffect(() => {
    if (analyticsEnabled && !hasTrackedSession.current) {
      trackPageView();
    }
  }, [analyticsEnabled, trackPageView]);

  // Track page views when pathname changes (if consent given)
  useEffect(() => {
    if (analyticsEnabled) {
      trackPageView();
    }
  }, [location.pathname, analyticsEnabled, trackPageView]);

  // Heartbeat and leave tracking
  useEffect(() => {
    if (!analyticsEnabled) {
      // Clear any existing heartbeat if analytics disabled
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    // Heartbeat to keep session active
    const sendHeartbeat = async () => {
      if (!isAnalyticsAllowed()) return;
      
      try {
        await fetch(`${API}/track/heartbeat?session_id=${sessionId.current}`, {
          method: 'POST'
        });
      } catch (error) {
        console.debug('Heartbeat error:', error);
      }
    };

    // Send heartbeat every 60 seconds
    heartbeatRef.current = setInterval(sendHeartbeat, 60000);

    // Handle page leave
    const handleLeave = async () => {
      if (!isAnalyticsAllowed()) return;
      
      try {
        // Use sendBeacon for reliability on page unload
        const url = `${API}/track/leave?session_id=${sessionId.current}`;
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url);
        } else {
          await fetch(url, { method: 'POST', keepalive: true });
        }
      } catch (error) {
        console.debug('Leave tracking error:', error);
      }
    };

    window.addEventListener('beforeunload', handleLeave);
    window.addEventListener('pagehide', handleLeave);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      window.removeEventListener('beforeunload', handleLeave);
      window.removeEventListener('pagehide', handleLeave);
    };
  }, [analyticsEnabled]);

  return null; // This component doesn't render anything
};

// Hook to manually trigger tracking (useful for custom events)
export const useAnalyticsTracker = () => {
  const trackEvent = useCallback(async (eventName, eventData = {}) => {
    if (!isAnalyticsAllowed()) {
      console.debug('Analytics tracking disabled - event not sent:', eventName);
      return false;
    }

    try {
      // You can extend this to send custom events to your backend
      console.debug('Analytics event:', eventName, eventData);
      return true;
    } catch (error) {
      console.debug('Event tracking error:', error);
      return false;
    }
  }, []);

  return { trackEvent, isEnabled: isAnalyticsAllowed() };
};
